import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '../../routing';
import { commandCenterApi } from '../../utils/api';
import { previousMonthRange } from '../../utils/period';
import Icon from '../../components/Icon';
import { LoadState, useRefetchOnFocus } from './parts';
import { BusinessFigures } from './businessFigures';
import { lastDayOfMonth, monthLabelOf } from './businessMonth';

const BUSINESS_PATH = '/command-center/business';

/**
 * מצב העסק — BE העסק's four headline figures for LAST month, on מבט על.
 *
 * Last month rather than this one because a month in progress is a partial
 * figure that reads as a bad month until its last day. The tiles are the same
 * `BusinessFigures` BE העסק renders, fed the same /crm payload, so the two
 * screens cannot disagree about a number.
 *
 * It fetches on its own and fails on its own: the overview's queues are the
 * reason ops open this screen, and a slow or failed books query must not hold
 * them back or blank them.
 *
 * The whole card is one link to BE העסק, opened on this card's month. The star
 * tile's ⋮ is deliberately absent here (no `onEditHeadcount`) — a menu inside a
 * link is a button inside a button, and editing belongs on the screen the card
 * routes to.
 */
export default function BusinessSnapshot() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    const { from, to } = previousMonthRange();
    try {
      const r = await commandCenterApi.getCrm(from, to);
      setData(r.data);
    } catch {
      setError(true);
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useRefetchOnFocus(load);

  if (error || !data) {
    return (
      <section className="cc-card cc-bizsnap is-static" aria-label="מצב העסק">
        <h2 className="cc-card-title">מצב העסק</h2>
        <LoadState loading={!error} error={error} onRetry={load} empty={false}>{null}</LoadState>
      </section>
    );
  }

  return (
    // Opens BE העסק on the SAME month this card shows (owner, 14/09/2026) — the
    // span the server resolved, not a recomputed "last month", so a card loaded
    // on the 31st and clicked on the 1st still lands on what it displayed.
    <button type="button" className="cc-card cc-bizsnap"
      onClick={() => navigate(`${BUSINESS_PATH}?from=${data.from}-01&to=${lastDayOfMonth(data.to)}`)}>
      <span className="cc-bizsnap-head">
        <span className="cc-card-title">מצב העסק</span>
        <span className="cc-bizsnap-period">{monthLabelOf(data.from)}</span>
        <Icon name="chevron-inline" size={15} className="cc-bizsnap-go" />
      </span>
      <BusinessFigures data={data} />
    </button>
  );
}
